// ===============================
// pages/ProductDetails.jsx
// ===============================

import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { CartContext } from "../../context/CartContext";
import "./ProductDetails.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const PRODUCT_API = `${API_BASE_URL}/api/products`;
const WISHLIST_API = `${API_BASE_URL}/api/wishlist`;
const FALLBACK_IMAGE = "https://via.placeholder.com/500x500?text=No+Image";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState("");
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (id && token) {
      checkWishlistStatus();
    } else {
      setIsWishlisted(false);
    }
  }, [id, token]);

  const resolveImageUrl = (img) => {
    if (!img || String(img).trim() === "") return FALLBACK_IMAGE;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    let cleanPath = img.replace(/^\/?uploads\//, "");
    const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    return `${baseUrl}/uploads/${cleanPath}`;
  };

  const getProductImages = (productData) => {
    if (Array.isArray(productData?.images) && productData.images.length > 0) {
      return productData.images.map((img) => resolveImageUrl(img));
    }
    if (productData?.image && String(productData.image).trim() !== "") {
      return [resolveImageUrl(productData.image)];
    }
    return [FALLBACK_IMAGE];
  };

  const getCategoryName = (category) => {
    if (!category) return "Uncategorized";
    if (typeof category === "object") return category.name || "Uncategorized";
    return category;
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${PRODUCT_API}/${id}`);
      const productData = res.data?.product || res.data;

      setProduct(productData);
      const imageList = getProductImages(productData);
      setSelectedImage(imageList[0]);
      setQuantity(1);
    } catch (error) {
      console.log(error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const checkWishlistStatus = async () => {
    try {
      const res = await axios.get(`${WISHLIST_API}/check/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsWishlisted(Boolean(res.data?.isWishlisted));
    } catch (error) {
      console.log(error);
      setIsWishlisted(false);
    }
  };

  const toggleWishlist = async () => {
    if (!token) {
      alert("Please login first.");
      navigate("/login");
      return;
    }

    try {
      setWishlistLoading(true);
      if (isWishlisted) {
        await axios.delete(`${WISHLIST_API}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(
          WISHLIST_API,
          { productId: id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      await checkWishlistStatus();
    } catch (error) {
      console.log(error);
      alert(error.response?.data?.message || "Wishlist action failed");
    } finally {
      setWishlistLoading(false);
    }
  };

  const increaseQty = () => {
    if (product?.stock && quantity < product.stock) {
      setQuantity((prev) => prev + 1);
    } else if (!product?.stock) {
      setQuantity((prev) => prev + 1);
    }
  };

  const decreaseQty = () => {
    if (quantity > 1) setQuantity((prev) => prev - 1);
  };

  const handleAddToCart = () => {
    if (product?.stock <= 0) {
      alert("This product is out of stock.");
      return;
    }
    addToCart({ ...product, quantity });
  };

  const handleBuyNow = () => {
    if (product?.stock <= 0) {
      alert("This product is out of stock.");
      return;
    }
    addToCart({ ...product, quantity });
    navigate("/cart");
  };

  if (loading) {
    return (
      <div className="product-loading">
        <h1>Loading Product...</h1>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-loading">
        <h1>Product Not Found</h1>
      </div>
    );
  }

  const productImages = getProductImages(product);

  return (
    <div className="product-details-page">
      <div className="container">
        
        {/* Cleaner Breadcrumb-style Topbar */}
        <div className="product-topbar">
          <span className="product-category">
            Home / {getCategoryName(product.category)}
          </span>
        </div>

        <div className="product-wrapper">
          
          {/* Left: Images */}
          <div className="product-left">
            <div className="main-image-card">
              <div className="main-image">
                <img src={selectedImage || FALLBACK_IMAGE} alt={product.name} />
              </div>
            </div>

            <div className="thumbnail-images">
              {productImages.map((img, index) => (
                <button
                  key={index}
                  type="button"
                  className={`thumbnail ${selectedImage === img ? "active-thumb" : ""}`}
                  onClick={() => setSelectedImage(img)}
                >
                  <img src={img} alt={`${product.name}-${index + 1}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Right: Info & Actions */}
          <div className="product-right">
            
            <div className="product-title-row">
              <div className="product-heading-block">
                <h1>{product.name}</h1>
              </div>
              
              <button
                className={`title-wishlist-btn ${isWishlisted ? "active-wishlist" : ""}`}
                onClick={toggleWishlist}
                type="button"
                disabled={wishlistLoading}
                aria-label="Toggle wishlist"
                title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
              >
                <i className={isWishlisted ? "fa-solid fa-heart" : "fa-regular fa-heart"}></i>
              </button>
            </div>

            {/* Price and Stock Grouped Together */}
            <div className="price-stock-row">
              <div className="price-section">
                <p className="product-price">₹{product.price}</p>
                <p className="price-note">Inclusive of all taxes</p>
              </div>
              
              <div className={`stock-badge ${product.stock > 0 ? "in-stock" : "out-stock"}`}>
                {product.stock > 0 ? `In Stock (${product.stock})` : "Out Of Stock"}
              </div>
            </div>

            <p className="product-description">
              {product.description || "No description available."}
            </p>

            <hr className="divider" />

            {/* Cleaner Purchase Panel */}
            <div className="purchase-panel">
              <div className="quantity-wrapper">
                <h4>Quantity</h4>
                <div className="quantity-box">
                  <button onClick={decreaseQty}>-</button>
                  <span>{quantity}</span>
                  <button onClick={increaseQty}>+</button>
                </div>
              </div>

              <div className="product-buttons">
                <button className="cart-btn" onClick={handleAddToCart} disabled={product.stock <= 0}>
                  Add To Cart
                </button>
                <button className="buy-btn" onClick={handleBuyNow} disabled={product.stock <= 0}>
                  Buy Now
                </button>
              </div>
            </div>

            {/* Minimalist Features List */}
            <div className="product-features">
              <h3>Why you'll like it</h3>
              <ul>
                <li>Premium quality and carefully selected finish.</li>
                <li>Fast delivery with secure packaging support.</li>
                <li>Easy return and refund assistance available.</li>
                <li>Safe checkout with trusted payment options.</li>
              </ul>
            </div>
            
            <p className="product-extra-note">
              <i className="fa-solid fa-shield-halved" style={{marginRight: "8px"}}></i>
              Secure payments, quick support, and order tracking after purchase.
            </p>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;